import React, { useState, useEffect } from 'react';

const ClubBreakdown = ({ sessions }) => {
  const [selectedClub, setSelectedClub] = useState('all');
  const [clubStats, setClubStats] = useState({});
  const [availableClubs, setAvailableClubs] = useState([]);

  useEffect(() => {
    if (sessions && sessions.length > 0) {
      // Extract all unique clubs
      const clubs = new Set();
      sessions.forEach(session => {
        session.shots.forEach(shot => {
          clubs.add(shot.club);
        });
      });
      
      setAvailableClubs([...clubs].sort());
      calculateClubStats();
    }
  }, [sessions]);

  useEffect(() => {
    calculateClubStats();
  }, [selectedClub, sessions]);

  const calculateClubStats = () => {
    if (!sessions || sessions.length === 0) {
      setClubStats({});
      return;
    }

    // Collect all shots
    let allShots = [];
    sessions.forEach(session => {
      allShots = [...allShots, ...session.shots];
    });

    // Filter by selected club if needed
    if (selectedClub !== 'all') {
      allShots = allShots.filter(shot => shot.club === selectedClub);
    }

    // Group shots by club
    const statsByClub = {};
    
    allShots.forEach(shot => {
      if (!statsByClub[shot.club]) {
        statsByClub[shot.club] = {
          totalShots: 0,
          totalDistance: 0,
          totalBallSpeed: 0,
          totalClubSpeed: 0,
          totalSmashFactor: 0,
          totalLaunchAngle: 0,
          totalSpinRate: 0,
          distances: [],
          ballSpeeds: [],
          clubSpeeds: [],
          smashFactors: [],
          launchAngles: [],
          spinRates: [],
          accuracies: []
        };
      }
      
      const stats = statsByClub[shot.club];
      stats.totalShots++;
      stats.totalDistance += shot.distance;
      stats.totalBallSpeed += shot.ballSpeed;
      stats.totalClubSpeed += shot.clubSpeed;
      stats.totalSmashFactor += shot.smashFactor;
      stats.totalLaunchAngle += shot.launchAngle;
      stats.totalSpinRate += shot.spinRate;
      
      stats.distances.push(shot.distance);
      stats.ballSpeeds.push(shot.ballSpeed);
      stats.clubSpeeds.push(shot.clubSpeed);
      stats.smashFactors.push(shot.smashFactor);
      stats.launchAngles.push(shot.launchAngle);
      stats.spinRates.push(shot.spinRate);
      stats.accuracies.push(shot.accuracy);
    });
    
    // Calculate averages and other stats
    Object.keys(statsByClub).forEach(club => {
      const stats = statsByClub[club];
      
      // Sort arrays for percentile calculations
      stats.distances.sort((a, b) => a - b);
      stats.ballSpeeds.sort((a, b) => a - b);
      stats.clubSpeeds.sort((a, b) => a - b);
      stats.smashFactors.sort((a, b) => a - b);
      stats.launchAngles.sort((a, b) => a - b);
      stats.spinRates.sort((a, b) => a - b);
      
      // Calculate averages
      stats.avgDistance = Math.round(stats.totalDistance / stats.totalShots);
      stats.avgBallSpeed = Math.round(stats.totalBallSpeed / stats.totalShots);
      stats.avgClubSpeed = Math.round(stats.totalClubSpeed / stats.totalShots);
      stats.avgSmashFactor = parseFloat((stats.totalSmashFactor / stats.totalShots).toFixed(2));
      stats.avgLaunchAngle = parseFloat((stats.totalLaunchAngle / stats.totalShots).toFixed(1));
      stats.avgSpinRate = Math.round(stats.totalSpinRate / stats.totalShots);
      
      // Calculate min/max
      stats.minDistance = stats.distances[0];
      stats.maxDistance = stats.distances[stats.distances.length - 1];
      
      // Calculate consistency (standard deviation of distances)
      const mean = stats.avgDistance;
      const squaredDiffs = stats.distances.map(d => Math.pow(d - mean, 2));
      const avgSquaredDiff = squaredDiffs.reduce((sum, val) => sum + val, 0) / stats.distances.length;
      stats.consistency = Math.round(Math.sqrt(avgSquaredDiff));
      
      // Calculate dispersion (standard deviation of accuracies)
      const accuracyMean = stats.accuracies.reduce((sum, val) => sum + val, 0) / stats.accuracies.length;
      const accuracySquaredDiffs = stats.accuracies.map(a => Math.pow(a - accuracyMean, 2));
      const accuracyAvgSquaredDiff = accuracySquaredDiffs.reduce((sum, val) => sum + val, 0) / stats.accuracies.length;
      stats.dispersion = Math.round(Math.sqrt(accuracyAvgSquaredDiff));
      
      // Calculate percentiles
      const getPercentile = (arr, percentile) => {
        const index = Math.ceil(arr.length * percentile) - 1;
        return arr[Math.max(0, Math.min(index, arr.length - 1))];
      };
      
      stats.p10Distance = getPercentile(stats.distances, 0.1);
      stats.p90Distance = getPercentile(stats.distances, 0.9);
      
      // Calculate carry consistency (difference between p90 and p10)
      stats.carryConsistency = stats.p90Distance - stats.p10Distance;
    });
    
    setClubStats(statsByClub);
  };

  const handleClubChange = (e) => {
    setSelectedClub(e.target.value);
  };

  return (
    <div className="club-breakdown">
      <div className="breakdown-header">
        <h2>Club Performance Analysis</h2>
        
        <div className="club-selector">
          <label htmlFor="clubSelect">Select Club:</label>
          <select 
            id="clubSelect" 
            value={selectedClub} 
            onChange={handleClubChange}
          >
            <option value="all">All Clubs</option>
            {availableClubs.map(club => (
              <option key={club} value={club}>{club}</option>
            ))}
          </select>
        </div>
      </div>
      
      {Object.keys(clubStats).length === 0 ? (
        <div className="no-data-message">
          <p>No data available for the selected club.</p>
          <p>Try selecting a different club or upload new session data.</p>
        </div>
      ) : (
        <div className="breakdown-content">
          <div className="clubs-grid">
            {Object.keys(clubStats).map(club => (
              <div key={club} className="club-card">
                <div className="club-header">
                  <h3>{club}</h3>
                  <div className="shot-count">{clubStats[club].totalShots} shots</div>
                </div>
                
                <div className="club-metrics">
                  <div className="metric-row">
                    <div className="metric">
                      <div className="metric-label">Avg Distance</div>
                      <div className="metric-value">{clubStats[club].avgDistance} <span className="unit">yds</span></div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-label">Range</div>
                      <div className="metric-value">
                        {clubStats[club].minDistance}-{clubStats[club].maxDistance} <span className="unit">yds</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="metric-row">
                    <div className="metric">
                      <div className="metric-label">Consistency</div>
                      <div className="metric-value">±{clubStats[club].consistency} <span className="unit">yds</span></div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-label">Dispersion</div>
                      <div className="metric-value">±{clubStats[club].dispersion} <span className="unit">yds</span></div>
                    </div>
                  </div>
                  
                  <div className="metric-row">
                    <div className="metric">
                      <div className="metric-label">Ball Speed</div>
                      <div className="metric-value">{clubStats[club].avgBallSpeed} <span className="unit">mph</span></div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-label">Club Speed</div>
                      <div className="metric-value">{clubStats[club].avgClubSpeed} <span className="unit">mph</span></div>
                    </div>
                  </div>
                  
                  <div className="metric-row">
                    <div className="metric">
                      <div className="metric-label">Smash Factor</div>
                      <div className="metric-value">{clubStats[club].avgSmashFactor}</div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-label">Launch Angle</div>
                      <div className="metric-value">{clubStats[club].avgLaunchAngle}°</div>
                    </div>
                  </div>
                </div>
                
                <div className="distance-distribution">
                  <div className="distribution-label">Distance Distribution</div>
                  <div className="distribution-bar">
                    <div className="p10-marker" style={{ left: '10%' }}>
                      <div className="marker-line"></div>
                      <div className="marker-label">{clubStats[club].p10Distance}</div>
                    </div>
                    <div className="avg-marker" style={{ left: '50%' }}>
                      <div className="marker-line"></div>
                      <div className="marker-label">{clubStats[club].avgDistance}</div>
                    </div>
                    <div className="p90-marker" style={{ left: '90%' }}>
                      <div className="marker-line"></div>
                      <div className="marker-label">{clubStats[club].p90Distance}</div>
                    </div>
                  </div>
                </div>
                
                <div className="club-insights">
                  <div className="insight-title">Insights:</div>
                  <ul className="insights-list">
                    {clubStats[club].carryConsistency > 30 && (
                      <li>High distance variation. Focus on consistent contact.</li>
                    )}
                    {clubStats[club].dispersion > 15 && (
                      <li>Wide dispersion. Work on alignment and swing path.</li>
                    )}
                    {clubStats[club].avgSmashFactor < 1.4 && club.includes('Driver') && (
                      <li>Low smash factor. Improve center face contact.</li>
                    )}
                    {clubStats[club].avgLaunchAngle < 10 && club.includes('Driver') && (
                      <li>Low launch angle. Consider adjusting tee height or attack angle.</li>
                    )}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubBreakdown;
